import React, {
	memo,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import classNames from "classnames";
import { createSelector } from "reselect";
import { useDispatch, useSelector } from "react-redux";

import styles from "./styles.module.scss";
import { timeSince, UseShowDescriptionWithMentions } from "utils";
import { useProfileGate } from "utils/useProfileGate";
import { DeleteEditComment } from "./components";
import { TStore } from "models";
import { api, deletePostComment, notify } from "store";
import { CommentInput } from "components";
import ReactionPicker from "components/ReactionPicker";
import ReactionSummary from "components/ReactionSummary";
import ReactorModal from "components/ReactorModal";
import { REACTIONS_ENABLED } from "config_environment";

const stateSelectorHandle = createSelector(
	(state: TStore) => state.auth.credentials?.user,
	(authenticatedUser) => ({
		authenticatedUser,
	}),
);

const checkTimestampForSecondsOrMinutes = new RegExp(
	"(\\d+)\\s(seconds?|minutes?)\\sago",
	"i",
);
interface ICommentBox {
	commentId: number;
	postId: number;
	avatar: any;
	boldName: string;
	text: string;
	time: string;
	lastCommentBox: boolean;
	likes?: any;
	tags?: any;
	user: {
		id: number;
		name: string;
		member_directory_opt_in?: boolean;
		profile_visible?: boolean;
	};
	mentions: string[];
	initialMyReaction?: string | null;
	initialReactionsSummary?: {
		counts: { type: string; count: number }[];
		total_count: number;
		recent_users: { id: number; full_name: string }[];
	};
}

const CommentBox: React.FunctionComponent<ICommentBox> = ({
	avatar,
	likes,
	boldName,
	tags,
	text,
	time,
	lastCommentBox = false,
	user,
	commentId,
	postId,
	mentions,
	initialMyReaction,
	initialReactionsSummary,
}) => {
	const dispatch = useDispatch();
	const [enableEdit, setEnableEdit] = useState(false);
	const stateSelector = useCallback(stateSelectorHandle, []);
	const { authenticatedUser } = useSelector(stateSelector);
	const profileGate = useProfileGate();
	const [commentTimestamp, setCommentTimestamp] = useState<string>("");
	const autoUpdateCommentTimestampIntervalId = useRef<
		NodeJS.Timer | undefined
	>(undefined);
	const [reactionCounts, setReactionCounts] = useState<
		{ type: string; count: number }[]
	>(initialReactionsSummary?.counts ?? []);
	const [reactionTotalCount, setReactionTotalCount] = useState<number>(
		initialReactionsSummary?.total_count ?? 0,
	);
	const [recentUsers, setRecentUsers] = useState<
		{ id: number; full_name: string }[]
	>(initialReactionsSummary?.recent_users ?? []);
	const [myReaction, setMyReaction] = useState<string | null>(
		initialMyReaction ?? null,
	);
	const [showReactorModal, setShowReactorModal] = useState(false);
	const [reactorModalFilter, setReactorModalFilter] = useState<string | undefined>();

	const handleShowReactors = useCallback((filterType?: string) => {
		setReactorModalFilter(filterType);
		setShowReactorModal(true);
	}, []);

	const handleReact = useCallback(
		async (type: string) => {
			if (!REACTIONS_ENABLED) return;
			// Save current state for rollback
			const prevMyReaction = myReaction;
			const prevCounts = reactionCounts;
			const prevTotalCount = reactionTotalCount;
			const prevRecentUsers = recentUsers;

			// Compute optimistic state
			const isRemovingReaction = myReaction === type;
			const isChangingReaction = myReaction !== null && myReaction !== type;

			let newCounts = [...reactionCounts.map((c) => ({ ...c }))];

			if (isRemovingReaction) {
				newCounts = newCounts
					.map((c) =>
						c.type === type ? { ...c, count: c.count - 1 } : c,
					)
					.filter((c) => c.count > 0);
			} else {
				if (isChangingReaction) {
					newCounts = newCounts
						.map((c) =>
							c.type === myReaction
								? { ...c, count: c.count - 1 }
								: c,
						)
						.filter((c) => c.count > 0);
				}
				const existing = newCounts.find((c) => c.type === type);
				if (existing) {
					existing.count += 1;
				} else {
					newCounts.push({ type, count: 1 });
				}
			}

			const newTotalCount = isRemovingReaction
				? reactionTotalCount - 1
				: isChangingReaction
					? reactionTotalCount
					: reactionTotalCount + 1;

			// Apply optimistic update immediately
			setMyReaction(isRemovingReaction ? null : type);
			setReactionCounts(newCounts);
			setReactionTotalCount(newTotalCount);

			try {
				const { data: response } = await api.reactions.toggle({
					reactable_type: "Comment",
					reactable_id: commentId,
					reaction_type: type,
				});
				// Reconcile with server response
				const result = response.data;
				setMyReaction(result.my_reaction ?? null);
				setReactionCounts(result.reactions_summary?.counts ?? []);
				setReactionTotalCount(result.reactions_summary?.total_count ?? 0);
				setRecentUsers(result.reactions_summary?.recent_users ?? []);
			} catch (error) {
				// Revert to previous state on error
				setMyReaction(prevMyReaction);
				setReactionCounts(prevCounts);
				setReactionTotalCount(prevTotalCount);
				setRecentUsers(prevRecentUsers);
				console.error("Failed to toggle reaction", error);
			}
		},
		[commentId, myReaction, reactionCounts, reactionTotalCount, recentUsers],
	);

	const parsedTimestamp: string = useMemo(() => {
		return timeSince(time);
	}, [time]);

	const handleDeleteComment = useCallback(() => {
		if (window.confirm("Are you sure you want to delete this comment?")) {
			dispatch(deletePostComment({ postId, commentId }));
		}
	}, [dispatch, postId, commentId]);

	const handleEditComment = useCallback(() => {
		setEnableEdit(!enableEdit);
	}, [enableEdit]);

	const handleEscapeKeyOnEdit = () => {
		setEnableEdit(false);
	};

	const handleCompleteEdit = () => {
		setEnableEdit(false);
	};

	const goToCommentAuthorProfilePage = useCallback(() => {
		if (!user) {
			notify("error", "An error occurred. Please try again later.");
			return;
		}
		profileGate.open({
			id: user.id,
			memberDirectoryOptIn: user.member_directory_opt_in,
			profileVisible: user.profile_visible,
		});
	}, [profileGate, user]);

	const updateTheCommentTimestamp = useCallback((timestamp: string) => {
		if (!timestamp) return;

		const instances = checkTimestampForSecondsOrMinutes.exec(timestamp);
		if (!instances) {
			setCommentTimestamp(timestamp);
			return;
		}

		if (instances[2].toLowerCase().indexOf("second") !== -1) {
			setCommentTimestamp("< 1 minute ago");
		} else {
			setCommentTimestamp(`${instances["input"]}`);
		}
		const newIntervalId = setInterval(() => {
			setCommentTimestamp((prevState) => {
				const instances =
					checkTimestampForSecondsOrMinutes.exec(prevState);

				if (!instances) return prevState;

				if (instances["input"] === "< 1 minute ago") {
					return "1 minute ago";
				} else {
					let minutes: number = parseInt(instances[1], 10) + 1;
					if (minutes === 60) {
						if (autoUpdateCommentTimestampIntervalId.current) {
							clearInterval(
								autoUpdateCommentTimestampIntervalId.current as any,
							);
							autoUpdateCommentTimestampIntervalId.current =
								undefined;
						}
						return "1 hour ago";
					} else {
						return `${minutes} minutes ago`;
					}
				}
			});
		}, 60000);
		autoUpdateCommentTimestampIntervalId.current = newIntervalId;
	}, []);

	useEffect(() => {
		if (!time) return;
		updateTheCommentTimestamp(parsedTimestamp);
	}, [updateTheCommentTimestamp, parsedTimestamp, time]);

	return (
		<div
			className={classNames(styles.commentBox, {
				[styles.lastCommentBox]: lastCommentBox,
			})}
		>
			<div className={styles.avatarSection}>
				<div
					className={styles.avatar}
					onClick={goToCommentAuthorProfilePage}
					style={{ cursor: "pointer" }}
				>
					{avatar}
				</div>
			</div>
			<div className={styles.commentInfo}>
				{boldName && commentTimestamp && (
					<div className={styles.inlineElements}>
						<h3
							onClick={goToCommentAuthorProfilePage}
							className={styles.commentAuthor}
						>
							{boldName}
						</h3>
						<div className={styles.commentTime}>
							{commentTimestamp}
						</div>
						<div className={styles.textAndBtnContainer}>
							{authenticatedUser?.id === user.id && (
								<DeleteEditComment
									onDeleteComment={handleDeleteComment}
									onEditComment={handleEditComment}
								/>
							)}
						</div>
					</div>
				)}
				{tags && (
					<span className={styles.primaryColorTags}>{tags}</span>
				)}
				{!enableEdit ? (
					<div className={styles.textWithEnters}>
						<UseShowDescriptionWithMentions
							text={text}
							mentions={mentions}
						/>
					</div>
				) : (
					<CommentInput
						autoFocus={true}
						postId={postId}
						commentId={commentId}
						text={text}
						handleEscapeKeyOnEdit={handleEscapeKeyOnEdit}
						handleDeleteComment={handleDeleteComment}
						handleCompleteEdit={handleCompleteEdit}
					/>
				)}
				{REACTIONS_ENABLED && (
				<div className={styles.commentReactions}>
					<ReactionSummary
						reactionsSummary={reactionCounts}
						recentUsers={recentUsers}
						totalCount={reactionTotalCount}
						myReaction={myReaction}
						currentUserId={authenticatedUser?.id}
						onShowReactors={handleShowReactors}
						onReact={handleReact}
						renderAfterBadges={
							<ReactionPicker
								onReact={handleReact}
								myReaction={myReaction}
								isComment
							/>
						}
						reactableType="Comment"
						reactableId={commentId}
						isPost={false}
					/>
				</div>
				)}
				{showReactorModal && REACTIONS_ENABLED && (
					<ReactorModal
						open={showReactorModal}
						onClose={() => setShowReactorModal(false)}
						reactableType="Comment"
						reactableId={commentId}
						reactionsSummary={reactionCounts}
						totalCount={reactionTotalCount}
						currentUserId={authenticatedUser?.id}
						initialFilter={reactorModalFilter}
					/>
				)}
				{profileGate.node}
			</div>
		</div>
	);
};

export default memo(CommentBox);
